import {
  draftToFilter,
  filterToDraft,
  type ColKind,
  type ColumnFilter,
  type FilterDraft,
} from "@/components/clicks/clicksGridEngine";
import { activeFieldKeys, fieldFilterOf, withFieldFilter, withoutFieldFilters } from "./filter-model";
import type { UrlActiveFilters, UrlFilterAnyMode } from "./url-active-filters";

/**
 * The "Filters" builder (owner, 30.10.2026; variant 25): every condition as a
 * row - a field's values, or a column's condition - edited as a draft and
 * turned back into chips on Apply.
 */
export type BuilderRow =
  | { id: number; type: "field"; key: string; mode: UrlFilterAnyMode; values: string[] }
  | { id: number; type: "column"; key: string; draft: FilterDraft }
  /** "+ Add condition" before a field is picked. */
  | { id: number; type: "new" };

let seq = 0;
export const nextRowId = (): number => ++seq;

/** The current filters as rows: fields in the bar's order, then column conditions. */
export const rowsFromState = (filters: UrlActiveFilters, columns: Record<string, ColumnFilter>): BuilderRow[] => [
  ...activeFieldKeys(filters).map((key): BuilderRow => {
    const f = fieldFilterOf(filters, key)!;
    return { id: nextRowId(), type: "field", key, mode: f.mode, values: f.values };
  }),
  ...Object.entries(columns).map(([key, f]): BuilderRow => ({ id: nextRowId(), type: "column", key, draft: filterToDraft(f) })),
];

/**
 * The rows back into the URL's two halves. Unfinished rows are dropped, a field
 * or column twice keeps the last row, and the bot preset stays as it was - it
 * is not the builder's.
 */
export function stateFromRows(
  rows: BuilderRow[],
  current: UrlActiveFilters,
  kindOf: (key: string) => ColKind,
): { filters: UrlActiveFilters; columns: Record<string, ColumnFilter> } {
  let filters = withoutFieldFilters(current);
  const columns: Record<string, ColumnFilter> = {};
  for (const r of rows) {
    if (r.type === "field" && r.values.length) filters = withFieldFilter(filters, r.key, { mode: r.mode, values: r.values });
    if (r.type === "column") {
      const f = draftToFilter(kindOf(r.key), r.draft);
      if (f) columns[r.key] = f;
    }
  }
  return { filters, columns };
}
